import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Check, X, Plus, RotateCcw } from 'lucide-react';
import SectionShell from '../plan/SectionShell';
import { jobBriefApi } from '../../../services/jobBriefApi';

/**
 * RestorationChecklist — Phase E.
 *
 * Users can add restoration items either from the seed catalog (chips) or
 * as free text. Each add/check/uncheck/remove is a timeline event.
 */
export default function RestorationChecklist({ draftId, closeoutState, onChanged, isReadOnly }) {
  const items = closeoutState?.restoration || [];
  const [catalog, setCatalog] = useState([]);
  const [customLabel, setCustomLabel] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    jobBriefApi.getRestorationCatalog().then((d) => setCatalog(d?.restoration || []));
  }, []);

  const alreadyAddedKeys = new Set(items.map((i) => i.key).filter(Boolean));

  const add = useCallback(async (payload) => {
    if (!draftId) return;
    setBusy('add');
    try {
      await jobBriefApi.addRestorationItem(draftId, payload);
      onChanged?.();
    } finally { setBusy(''); }
  }, [draftId, onChanged]);

  const addCustom = useCallback(async () => {
    const v = customLabel.trim();
    if (!v) return;
    await add({ label: v, category: 'custom' });
    setCustomLabel('');
  }, [customLabel, add]);

  const toggle = useCallback(async (item) => {
    if (!draftId) return;
    setBusy(item.item_id);
    try {
      if (item.completed) await jobBriefApi.uncheckRestorationItem(draftId, item.item_id);
      else                await jobBriefApi.checkRestorationItem(draftId, item.item_id, {});
      onChanged?.();
    } finally { setBusy(''); }
  }, [draftId, onChanged]);

  const remove = useCallback(async (item) => {
    if (!draftId) return;
    setBusy(item.item_id);
    try {
      await jobBriefApi.removeRestorationItem(draftId, item.item_id);
      onChanged?.();
    } finally { setBusy(''); }
  }, [draftId, onChanged]);

  const catalogChipsUnadded = catalog.filter((c) => !alreadyAddedKeys.has(c.key));

  return (
    <SectionShell
      icon={ClipboardList}
      title="Restoration Checklist"
      subtitle="Every step required to return the site + equipment to normal service. Tap catalog chips to add, or write custom."
      color="#86efac"
      badge={items.length
        ? `${items.filter((i) => i.completed).length}/${items.length} done`
        : 'Nothing added'}
      testId="jbv2-section-restoration"
    >
      {items.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-restoration-empty">
          No restoration items yet. Add from the catalog below or type a custom item.
        </div>
      )}

      {items.map((it) => (
        <div
          key={it.item_id}
          className={`jbv2-row${it.completed ? ' jbv2-row--dismissed' : ''}`}
          data-testid={`jbv2-restoration-row-${it.item_id}`}
        >
          <div className="jbv2-row-body">
            <div className="jbv2-row-cat">{it.label}</div>
            {it.category && <div className="jbv2-hp-row-meta">{it.category}</div>}
            {it.completed && (
              <div className="jbv2-hp-row-meta" data-testid={`jbv2-restoration-completed-meta-${it.item_id}`}>
                Completed by {it.completed_by}
                {it.note && <> — &ldquo;{it.note}&rdquo;</>}
              </div>
            )}
          </div>
          {!isReadOnly && (
            <div className="jbv2-row-actions">
              <button
                type="button"
                className="jbv2-row-btn"
                onClick={() => toggle(it)}
                disabled={busy === it.item_id}
                data-testid={`jbv2-restoration-toggle-${it.item_id}`}
              >
                {it.completed ? <><RotateCcw size={11} /> Undo</> : <><Check size={11} /> Complete</>}
              </button>
              <button
                type="button"
                className="jbv2-row-btn jbv2-row-btn--danger"
                onClick={() => remove(it)}
                disabled={busy === it.item_id}
                data-testid={`jbv2-restoration-remove-${it.item_id}`}
                aria-label="Remove"
              >
                <X size={11} />
              </button>
            </div>
          )}
        </div>
      ))}

      {!isReadOnly && catalogChipsUnadded.length > 0 && (
        <>
          <div className="jbv2-field-label" style={{ marginTop: 4 }}>Suggestions</div>
          <div className="jbv2-chips" data-testid="jbv2-restoration-catalog-chips">
            {catalogChipsUnadded.map((c) => (
              <button
                key={c.key}
                type="button"
                className="jbv2-chip"
                onClick={() => add({ label: c.label, key: c.key, category: c.category })}
                data-testid={`jbv2-restoration-catalog-${c.key}`}
              >
                <Plus size={12} /> {c.label}
              </button>
            ))}
          </div>
        </>
      )}

      {!isReadOnly && (
        <div className="jbv2-add-row" data-testid="jbv2-restoration-add-row">
          <input
            type="text"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            placeholder="Custom restoration item"
            data-testid="jbv2-restoration-custom-input"
            aria-label="Custom restoration item"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={busy === 'add' || !customLabel.trim()}
            data-testid="jbv2-restoration-custom-add"
          >
            <Plus size={13} /> Add
          </button>
        </div>
      )}
    </SectionShell>
  );
}
